// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/react/
import * as Sentry from "@sentry/react"
import { reactRouterBrowserTracingIntegration } from "@sentry/react/react-router"

import { IS_PROD } from "@/lib/constants"

// Only run browser sentry in production
if (IS_PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,

    integrations: [
      Sentry.browserProfilingIntegration(),
      reactRouterBrowserTracingIntegration(),
    ],

    // Microsoft Outlook's SafeLinks crawler scans URLs in emails and injects
    // a script that throws this unhandled rejection. It's not an app bug.
    //
    // A bare "Failed to fetch" (optionally suffixed with the request's
    // hostname, e.g. "Failed to fetch (tile.googleapis.com)") is Chrome's
    // generic message for a network-level fetch failure with no further
    // detail available — most commonly a request aborted because the
    // triggering component unmounted or the user navigated away while it was
    // in flight (e.g. the Google tile-session fetch issued internally by
    // `ol/source/Google` in EditableMap). It carries no actionable signal
    // and third-party library code can throw it without giving app code a
    // hook to catch it, so we drop it here instead of chasing each instance.
    //
    // mapbox-gl's telemetry callback can fire after `Map.remove()` has nulled
    // `errorCb` (a failed telemetry POST resolving after the map unmounted).
    // It's a race inside mapbox-gl, not app code, and we can't catch it.
    ignoreErrors: [
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
      /^Failed to fetch(\s\([^)]*\))?$/,
      /^this\.errorCb is not a function$/,
    ],

    // Drop errors thrown by scripts served from the Wayback Machine. Archived
    // copies of our bundles embed our DSN and phone home when replayed, and
    // web.archive.org's replay shim can spoof window.location, so we match on
    // the stack-frame script URLs instead.
    denyUrls: [/https?:\/\/web\.archive\.org/i],

    // Drop events from pages not served on our own domain (archive.org
    // replays, translation proxies, scrapers re-hosting our bundle).
    beforeSend(event) {
      const { hostname } = window.location
      const allowedApexDomains = ["getlumen.com", "lumen.energy"]
      if (
        allowedApexDomains.some(
          (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
        )
      ) {
        return event
      }
      return null
    },

    tracesSampleRate: 1.0, // Capture 100% of traces.
    // Set `tracePropagationTargets` to declare which URL(s) should have trace propagation enabled
    tracePropagationTargets: [/^\//, /^https:\/\/getlumen\.com\/graphql/],

    profileSessionSampleRate: 1.0,
  })
}
